import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import * as RPNInput from 'react-phone-number-input'
import flags from 'react-phone-number-input/flags'
import en from 'react-phone-number-input/locale/en'
import fr from 'react-phone-number-input/locale/fr'

import ChevronUpDownIcon from '../../../../icons/chevron-updown'
import Typography from '../../typography'
import { InputProps } from '..'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from './command'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

type Label = {
  placeholder: string
  notFound: string
}

const inputRef =
  React.createRef<HTMLInputElement | null>() as React.MutableRefObject<HTMLInputElement | null>

// Language Context
const LanguageContext = createContext<AvailableLanguages>('fr')

// Hook to use Language Context
const useLanguage = () => useContext(LanguageContext)

const labelsMap: Record<AvailableLanguages, Record<string, string>> = {
  fr,
  en
}

const mapLangugeToLabels: Record<AvailableLanguages, Label> = {
  fr: {
    placeholder: 'Rechercher un pays...',
    notFound: 'Aucun pays trouvé'
  },
  en: {
    placeholder: 'Search for a country...',
    notFound: 'No country found'
  }
}

type CountrySelectOption = { label: string; value: RPNInput.Country }

type CountrySelectProps = {
  value: RPNInput.Country
  onChange: (value: RPNInput.Country) => void
  options: CountrySelectOption[]
  disabled?: boolean
}

export type PhoneInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'value'
> &
  Omit<RPNInput.Props<typeof RPNInput.default>, 'onChange' | 'value'> & {
    onChange?: (value: RPNInput.Value | string | null) => void
    value?: string | RPNInput.Value | null | undefined
    hasError?: boolean
    label?: string
    language?: AvailableLanguages
    required?: boolean
  }

export const FlagComponent = ({
  country,
  countryName,
  shape = 'default'
}: RPNInput.FlagProps & { shape?: 'rounded' | 'default' }) => {
  const Flag = flags[country]

  return (
    <div
      className={cn(' flex h-6 w-6 rounded-full', {
        '"inline-block h-7 w-7 [clip-path:circle(30%)] [&>svg]:h-full [&>svg]:w-full [&>svg]:block"':
          shape === 'rounded'
      })}
    >
      {Flag && <Flag title={countryName} />}
    </div>
  )
}

const InputComponent = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...props }, ref) => {
    const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
      const input = e.currentTarget
      input.value = input.value.replace(/[^+0-9]/g, '')
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      props.onBlur?.(e)
    }

    const combinedRef = (node: HTMLInputElement) => {
      inputRef.current = node
      if (typeof ref === 'function') {
        ref(node)
      } else if (ref) {
        ref.current = node
      }
    }

    return (
      <input
        ref={combinedRef}
        className={cn(
          'relative w-full rounded min-h-[37px] focus:outline-none text-xs font-medium placeholder:text-grey-light-active placeholder:font-light',
          className
        )}
        {...props}
        value={props.value ?? ''}
        inputMode="numeric"
        onInput={handleInput}
        onBlur={handleBlur}
      />
    )
  }
)
InputComponent.displayName = 'InputComponent'

FlagComponent.displayName = 'FlagComponent'
const CountrySelect = ({ value, onChange, options, disabled }: CountrySelectProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const language = useLanguage() // Use the language from context

  const handleSelect = useCallback(
    (country: RPNInput.Country) => {
      onChange(country)
      setIsPopoverOpen(false) // Close the popover

      // Focus the input field after selection
      setTimeout(() => {
        inputRef.current?.focus()
      }, 50) // await for the popover to close
    },
    [onChange, inputRef]
  )

  // Override the default filter function to keep France at the top of the list
  const handleFilter = (value: string, search: string) => {
    const matchesSearch = value.toLowerCase().includes(search.toLowerCase())
    return matchesSearch ? 1 : 0 // Return 1 to include, 0 to exclude
  }

  // To keep france at the top of the list
  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => (a.value === 'FR' ? -1 : b.value === 'FR' ? 1 : 0)),
    [options]
  )
  return (
    <Popover modal open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={'flex gap-2 rounded-e-none rounded-s-lg pl-3 items-center'}
          disabled={disabled}
        >
          <FlagComponent country={value} countryName={value} />
          <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
            {value}
          </Typography>
          <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
            {value && `+${RPNInput.getCountryCallingCode(value)}`}
          </Typography>
          <ChevronUpDownIcon color="rgb(217 217 217)" />
        </button>
      </PopoverTrigger>
      <PopoverContent className={'w-[300px] p-0 bg-white'}>
        <Command filter={handleFilter}>
          <CommandList>
            <CommandInput placeholder={mapLangugeToLabels[language].placeholder} />
            <CommandEmpty>{mapLangugeToLabels[language].notFound}</CommandEmpty>
            <CommandGroup>
              {sortedOptions
                .filter((x) => x.value)
                .map((option) => (
                  <CommandItem
                    className={'gap-2 hover:bg-grey-light cursor-pointer'}
                    key={option.value}
                    onSelect={() => handleSelect(option.value)}
                  >
                    <FlagComponent country={option.value} countryName={option.label} />
                    <span className={'flex-1 text-sm'}>{option.label}</span>
                    {option.value && (
                      <span className={'text-foreground/50 text-sm'}>
                        {`+${RPNInput.getCountryCallingCode(option.value)}`}
                      </span>
                    )}
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> = React.forwardRef<
  React.ElementRef<typeof RPNInput.default>,
  PhoneInputProps
>(
  (
    {
      className,
      onChange,
      hasError,
      placeholder = 'ex : 06 00 00 00 00',
      language = 'fr',
      disabled,
      defaultCountry,
      value,
      ...props
    },
    ref
  ) => {
    const labels = labelsMap[language] || fr

    return (
      <LanguageContext.Provider value={language}>
        <RPNInput.default
          {...props}
          ref={ref}
          value={value ?? undefined}
          defaultCountry={defaultCountry || 'FR'}
          placeholder={placeholder}
          disabled={disabled}
          labels={labels}
          initialValueFormat="national"
          focusInputOnCountrySelection={true}
          className={cn('flex', className)}
          flagComponent={FlagComponent}
          countrySelectComponent={CountrySelect}
          inputComponent={InputComponent}
          style={{
            borderColor: hasError ? tailwindColors.red.DEFAULT : tailwindColors.grey.stroke,
            borderWidth: '1px',
            borderRadius: '12px',
            opacity: disabled ? 0.5 : 1
          }}
          onChange={(value) => {
            onChange?.(value || null)
          }}
        />
      </LanguageContext.Provider>
    )
  }
)
PhoneInput.displayName = 'PhoneInput'

export { PhoneInput }
